/**
 * The overview, as a state a screen can render.
 *
 * The query's mechanics — pending, error, data — are turned into named states
 * here so the page never has to decide what a combination of booleans means. A
 * page that decided would eventually decide differently from the next page.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { readOverview } from "@/shared/api";
import type { OverviewResponse } from "@/shared/api";
import { statusOf } from "@/shared/http/errors";

/**
 * A range the owner picked, as the server wants it.
 *
 * Sent as written and normalised on the server, because the browser knows a
 * timezone and the ledger knows only UTC. Turning a local moment into UTC here
 * would put the conversion in the one place that cannot be tested against the
 * record it is converting for.
 */
export type Range = {
  readonly since: string;
  readonly until: string;
  /**
   * What to call it on screen, when the reader picked something with a name.
   *
   * A calendar month is a range like any other to the server — two moments in
   * UTC — but "Август 2026" and "Свой период" are not equally useful to read,
   * and only the side that picked it knows which it was.
   */
  readonly label?: string;
};

export const OVERVIEW_KEY = ["overview"] as const;

/** The query key has to carry the range, or a switch would show cached rows. */
export function overviewKey(range: Range | null): readonly unknown[] {
  return range === null ? OVERVIEW_KEY : [...OVERVIEW_KEY, range.since, range.until];
}

/** Two missed reads: past this the page has not heard from the server. */
export const UNHEARD_AFTER_MS = 30_000;

/**
 * The overview as of now, not as of the last answer.
 *
 * Offline, the query pauses without an error and its last answer stays
 * "ready"; every label read its frozen age and "fresh" from it (audit A11).
 * Once nothing has been heard for two reads, the age keeps counting from the
 * moment it was received and the figures are marked stale: last known, not
 * live. One place, so every screen that reads the overview agrees.
 */
export function asOfNow(
  overview: OverviewResponse,
  receivedAt: number,
  now: number,
): OverviewResponse {
  const waited = Math.max(0, now - receivedAt);
  if (waited <= UNHEARD_AFTER_MS) return overview;
  return {
    ...overview,
    age_seconds: overview.age_seconds + Math.floor(waited / 1000),
    stale: true,
  };
}

/** A clock that ticks while the page is open, so an age can keep counting. */
function useNow(everyMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, everyMs);
    return () => {
      clearInterval(timer);
    };
  }, [everyMs]);
  return now;
}

export type OverviewState =
  | { readonly status: "loading" }
  | { readonly status: "forbidden" }
  | { readonly status: "unreadable" }
  | { readonly status: "not-observed" }
  | { readonly status: "ready"; readonly overview: OverviewResponse };

export function useOverview(range: Range | null = null): OverviewState {
  const query = useQuery({
    queryKey: overviewKey(range),
    queryFn: async (): Promise<OverviewResponse> => {
      const { data, error, response } = await readOverview(
        range === null ? {} : { query: { since: range.since, until: range.until } },
      );
      if (error !== undefined || data === undefined) {
        throw Object.assign(new Error("overview unreadable"), {
          status: response?.status,
        });
      }
      return data;
    },
    // Re-read on a timer: the figures move, and a screen that only updated
    // when somebody pressed something would be a screen somebody trusts while
    // it is wrong. The payload says how fresh it is either way.
    refetchInterval: 15_000,
    // No retries. The timer above already tries again shortly, and retrying
    // in place only leaves a spinner on screen while the owner waits to be
    // told something is wrong. Being told quickly is the point.
    retry: false,
  });
  const now = useNow(5_000);

  if (query.isPending) return { status: "loading" };
  if (query.isError) {
    // 403 is not 401. The session is fine and this is not allowed, and
    // signing somebody out for it would hide the real answer.
    const status = statusOf(query.error);
    if (status === 403) return { status: "forbidden" };
    // 409: the account has never been read on its exchange. A state of the
    // account, not a failure of this read, and it is shown as one.
    if (status === 409) return { status: "not-observed" };
    return { status: "unreadable" };
  }
  return {
    status: "ready",
    overview: asOfNow(query.data, query.dataUpdatedAt, Math.max(now, Date.now())),
  };
}
